import * as React from 'react';
import Tag from '../../Tag/Tag';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Spinner from '@mulesoft/anypoint-components/lib/Spinner';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import IconPanel from '@mulesoft/anypoint-icons/lib/Icon';
import FixedTable from '@mulesoft/anypoint-components/lib/FixedTable';
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip';
import * as labels from '@mulesoft/monitoring-center-components/lib/utils/labels'
import { Urls } from '../../Sidebar/Urls';
import { Dashboard } from '../../Dashboard/model';
import { Link, NavLink } from 'react-router-dom';
import { BuiltinSvg } from '../../BuiltInDashboard/svgs/BuiltinSvg'
import './CustomDashboardList.css'

export type ValueProps = {
  dashboards: Dashboard[]
  loading: boolean
  query: string
  hasEditRole?: boolean
}

export type EventProps = {
  fetchDashboards: () => void
  queryChange: (query: string) => void
}

interface StateProps {
  resourceType: string
}

export type Props = ValueProps & EventProps

export default class CustomDashboardList extends React.Component<Props, StateProps> {
  private onChangeQuery = this.onChange.bind(this);

  constructor(props: Props){
    super(props);
    this.state = { resourceType: 'app'};
  }

  private columns = [
    {
      key: 'name',
      name: 'Name',
    }, {
      key: 'tags',
      name: 'Tags',
      disableSort: true
    }
  ];

  private getDashboardItems(dashboards: Dashboard[]): { name: JSX.Element, tags?: JSX.Element }[] {
    if (!dashboards) {
      return []
    }

    return dashboards.map(dash => {
      return {
        name: CustomDashboardList.dashboardNameItem(dash.title, dash.isStarred, dash.uri),
        tags: CustomDashboardList.tagItem(dash.tags)
      }
    });
  }

  static dashboardNameItem(title: string, starred: boolean, uri: string) {
    return (
      <div className={'title-column'}>
        {CustomDashboardList.renderStar(starred)}
        <Link to={`${Urls.customDashboardPage}/${uri.replace('db/', '')}`}>{title}</Link>
      </div>
    )
  }

  static tagItem(tags: string[] | undefined) {
    if (!tags) return undefined;

    return (
      <div className={'tags-container'}>
        {
          tags.map(t => {
            return <Tag key={t} title={t}/>
          })}
      </div>
    )
  }

  componentDidMount() {
    this.props.fetchDashboards()
  }

  onChange(query: { value: string }) {
    this.props.queryChange(query.value)
  }

  render() {
    const {dashboards, loading, query, hasEditRole} = this.props;
    const editDisabled = !hasEditRole
    const dashboardItems = this.getDashboardItems(dashboards);

    if (dashboards.length > 0 || query) {
      return CustomDashboardList.renderDashboardsList(this.onChangeQuery, query, this.columns, dashboardItems,
                                                      loading, editDisabled);
    } else if (loading) {
      return CustomDashboardList.renderSpinner();
    } else {
      return CustomDashboardList.renderEmptyState(editDisabled);
    }
  }

  static renderStar(starred: boolean) {
    const starredMark = <mark className={'starred-icon-list starred'}>★</mark>
    const noStarredMark = <mark className={'starred-icon-list nonStarred'}>☆</mark>

    return starred ? starredMark : noStarredMark
  }

  static renderCustomDashboardButton(editDisabled: boolean) {
    const customDashboardButton = (
      <NavLink className={`new-dashboard-link ${editDisabled ? 'disabled' : ''}`} to={Urls.newCustomDashboard}>
        <Button
          kind={'primary'}
          testId={'new-dashboard-btn'}
          disabled={editDisabled}
        >
          <IconPanel testId={'new-dashboard-button-icon'} name={'plus-small'} size={14}/>
          <span>New Dashboard</span>
        </Button>
      </NavLink>
    )
    
    return editDisabled ? (
      <Tooltip content={labels.missingDashboardPermissions} position='bottom' offset={[50, 10]}>
        {customDashboardButton}
      </Tooltip>
    ) : customDashboardButton
  }

  static renderDashboardsList(onChangeQuery: any, query: any, columns: any, dashboardItems: any, loading: boolean, editDisabled: boolean) {  
    return (
      <div className={'custom-dashboard-list'}>
        <div className={'custom-dashboard-header'}>
          <div className={'custom-dashboard-button'}>
            {this.renderCustomDashboardButton(editDisabled)}
          </div>
          <div className={'custom-dashboard-select'}>
            <TextField
              placeholder="Search custom dashboards..."
              type="search"
              testId="search-text-field"
              onChange={onChangeQuery}
              value={query}
            />
          </div>
        </div>
        <div className={'custom-table-container'}>
          <FixedTable className={'no-scrollbar'} columns={columns} data={dashboardItems} isLoading={loading}/>
        </div>
      </div>
    )
  }

  static renderSpinner() {
    return (
      <div className={'custom-dashboards-loading'}>
        <Spinner size="m"/>
      </div>
    )
  }

  static renderEmptyState(editDisabled: boolean) {
    return (
      <div className={'custom-dashboards-landing-page-container'}>
        <div className={'custom-landing-page-header'}>
          <span className={'landing-page-title'}>Using custom dashboards</span>
          <span className={'landing-page-subtitle'}>
              Create your own custom dashboards across multiple resources and environments
              to provide instant visibility into the performance of your network.
          </span>
        </div>
        <div className={'landing-page-button'}>
          {this.renderCustomDashboardButton(editDisabled)}
        </div>
        <div className={'custom-dashboards-landing-image'}>
          <BuiltinSvg/>
        </div>
      </div>
    )
  }
}
